// external
import React, { useEffect, useState, useContext } from 'react';
import SimpleBar from 'simplebar-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { ChevronDown } from 'react-feather'

import { AnimatePresence, motion} from 'framer-motion';

//components
import Dropdown from '../global/Dropdown';
import UserResult from './UserResult';
import ModalWrapper from  '@/components/modals/ModalWrapper';
import { ToastData } from  '@/interfaces/LockerDetails';
import ToastNotification, { ToastMessageType } from '../global/ToastNotification';

//hooks
import useHTTP from  '@/components/hooks/useHTTP';

//context
import { PlannerContext } from '@/context/PlannerContext';

//interfaces
import { user, plannerEventCount, plannerEvent, MaxCapType, occurrenceType, eventReplyStatusEnum } from  '@/interfaces/planner';

//utilities
import { BuildPath } from  '@/helpers/Paths';
import { timeToDecimal } from  '@/helpers/Date';
import { makePlural } from  '@/helpers/Text';

import '../../../css/invitees.scss'


interface InviteesViewEditProps {
    setAllSelectedUsers:(user:user[]) => void,
    eventCount:plannerEventCount,
    event:plannerEvent,
    isGatedEvent: boolean,
}

const InviteesViewEdit = ({ eventCount, event, isGatedEvent} : InviteesViewEditProps) => {

    const [loading, setLoading] = useState<boolean>(false);
    const [toast, setToast] = useState<ToastData>({show: false, message: undefined, type: undefined});
    const [usersConfirmed, setUsersConfirmed] = useState<user[]>([]);
    const [usersDeclined, setUsersDeclined] = useState<user[]>([]);
    const [usersAwaiting, setUsersAwaiting] = useState<user[]>([]);
    const [usersAwaitingApproval, setUsersAwaitingApproval] = useState<user[]>([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    // locally tracked reg status, set from app
    const [registrationStatus, setRegistrationStatus] = useState<'Open' | 'Closed'>(event.registrationStatus);
    // server managed reg status. set on successfuly server updates
    const [plannerEventRegistrationStatus, setPlannerEventRegistrationStatus] = useState<'Open' | 'Closed'>(event.registrationStatus)

    const [processingUsers, setProccessingUsers] = useState<number[]>([])
    
    const {get, put} = useHTTP();

    const { selectedCal, calendarMessage } = useContext(PlannerContext)

    const [updatingRegistrationStatus, setUpdatingRegistrationStatus] = useState<boolean>(false)
   
    useEffect(() => {
        if(calendarMessage.timestamp) {
            getInvitees(false);
            setProccessingUsers([]);
        }
    }, [calendarMessage.timestamp])

    
    function setPlannerEventRegistrationStatusAsync(status: 'Open' | 'Closed'){
        setUpdatingRegistrationStatus(true)
        put(`planner/calendar/${selectedCal.value}/events/${event.id}/registration`, {registrationStatus: status}).then(() => {
            setPlannerEventRegistrationStatus(status);
        }).catch(() => {
            setRegistrationStatus(status === 'Open' ? 'Closed' : 'Open')
            setToast({type: ToastMessageType.WARNING, message: `Could not update event registration status at this time, please try again later`, show: true})
        })
        .finally(()=> {
            setUpdatingRegistrationStatus(false)
        })
        ;
    }

    useEffect(() => {
        if (plannerEventRegistrationStatus !== registrationStatus) {
            setPlannerEventRegistrationStatusAsync(registrationStatus)
        }
        
    }, [registrationStatus])

    function setGatedEvents(users: any[]){
        const awaiting = [];
        const confirmed = [];
        const rejected = [];
        const approvalRequired = [];

        for(const user of users) {
            switch(user.eventReplyStatus) {
                case eventReplyStatusEnum.Attending:
                    confirmed.push(user);
                    break;
                case eventReplyStatusEnum.NotAttending:
                    rejected.push(user);
                    break;
                case eventReplyStatusEnum.PendingApproval:
                    approvalRequired.push(user);
                    break;  
                default:
                    awaiting.push(user);
                    break;
            }
        }

        setUsersAwaiting(awaiting);
        setUsersConfirmed(confirmed);
        setUsersDeclined(rejected);
        setUsersAwaitingApproval(approvalRequired);
    }

    function getInvitees(loadingState=true){
        get(`planner/calendar/${selectedCal.value}/events/${event.id}/invitees?skip=0&take=1000`).then(({data}:{data:any}) => {
            loadingState && setLoading(true)
            
            if(data.items) {
                if(isGatedEvent){
                    setGatedEvents(data.items)
                }
                else {
                    setUsersConfirmed(data.items.filter(x => x.eventReplyStatus === eventReplyStatusEnum.Attending)) 
                    setUsersDeclined(data.items.filter(x => x.eventReplyStatus === eventReplyStatusEnum.NotAttending)) 
                    setUsersAwaiting(data.items.filter(x => x.eventReplyStatus === eventReplyStatusEnum.AwaitingStatus || x.eventReplyStatus === eventReplyStatusEnum.PendingApproval)) 
                }
            } 
        }).catch(err => {
            console.log(err, "err")
        }).finally(() => {
            loadingState && setLoading(false)
        })
    }

    useEffect(() => {

        if (event){
            getInvitees();
            setRegistrationStatus(event.registrationStatus);
        }   

    }, [event.id])

    function calculateAttendancePercent(){
        if(event.maximumCapacityType === MaxCapType.attendees) {
            return Math.floor((event.currentAttendingCount / event.maximumCapacity) * 100)
        }
        else {
            return Math.floor((timeToDecimal(event.currentTimeAllocated) / event.maximumCapacity) * 100)
        } 
    }

    function toggleRegistration(val: 'Open' | 'Closed'){
        //short circuit if value doesn't change
        if(registrationStatus === val) {
            return;
        }

        if(val === 'Closed') {
            setShowConfirmationModal(true);
        }
        else {
            setRegistrationStatus(val)
        }   
    }

    function getProgressTooltipText(){
        if(event.maximumCapacityType === MaxCapType.attendees) {
            return `${event.currentAttendingCount} of ${event.maximumCapacity} spaces taken`
        }
        else {
            return `${timeToDecimal(event.currentTimeAllocated)} ${makePlural(timeToDecimal(event.currentTimeAllocated), 'hour')} of ${event.maximumCapacity} hours taken`
        }
    }

    function eventIsFull(){
        if(event.maximumCapacityType === MaxCapType.attendees){
            return event.currentAttendingCount >= event.maximumCapacity
        }
        else if (event.maximumCapacityType === MaxCapType.time) {
            return (timeToDecimal(event.currentTimeAllocated) >= event.maximumCapacity)
        }
        return false;
        
    }

    function isEventConfigurable(){
        return [occurrenceType.AllDayConfigurable, occurrenceType.StartEndConfigurable].includes(event.occurrence.occurrenceType) 
    }

    return (
        <div className='invitees invitees-view'>

            <div className='invitees-view-title-wrapper'>
                <h2 className='invitiees-title'>Invitees ({eventCount.totalCount})</h2>
                {isGatedEvent && 
                    <Dropdown
                        className={'registrations-toggle-dropdown'}
                        options={[{value:'Open', label: 'Open'}, {value: 'Closed', label: 'Closed'}]}
                        defaultValue={plannerEventRegistrationStatus}
                        setValue={(val) => toggleRegistration(val as 'Open' | 'Closed')}
                        buttonText={updatingRegistrationStatus 
                            ? <> Updating Event… <img className="spinner" src={`${BuildPath()}/images/spinner.gif`} alt="loading..." style={{height: '10px', background: 'white', position: 'absolute'}} /></>
                            : <>Registrations: <span style={{fontWeight: 600}}>{registrationStatus}</span></>
                        }
                        icon={<ChevronDown size={12} color={'#657d98'} />}
                        isDisabled={(eventIsFull() || updatingRegistrationStatus) && plannerEventRegistrationStatus === 'Closed'}
                    />
                }
            </div>

            <section style={{display:'flex'}} className='attendees-summary'>
                <div aria-label={`Attending (${eventCount.attendingCount})`}>
                    <img src={`${BuildPath()}/images/planner/user-check.svg`} alt={`${eventCount.attendingCount} ${makePlural(eventCount.attendingCount, 'user')} attending`} /> 
                    <span>{eventCount.attendingCount}</span>
                </div>
                <div aria-label={`Not Attend (${eventCount.declinedCount})`}>
                    <img src={`${BuildPath()}/images/planner/user-x.svg`} alt={`${eventCount.declinedCount} ${makePlural(eventCount.declinedCount, 'user')} not attending`} /> 
                    <span>{eventCount.declinedCount}</span>
                </div>
                <div aria-label={`No Response (${eventCount.awaitingResponseCount})`}>
                    <img src={`${BuildPath()}/images/planner/user-unknown.svg`} alt={`${eventCount.awaitingResponseCount} ${makePlural(eventCount.awaitingResponseCount, 'user')} have yet to respond`} /> 
                    <span>{eventCount.awaitingResponseCount}</span>
                </div>
            </section>

            {event.maximumCapacity && 
             <>                   
                <ReactTooltip id={'progress-tooltip'} place="bottom" className='thrive-tooltip event-modal-tt' >
                    <span>{getProgressTooltipText()}</span>
                </ReactTooltip>
                <div className='progress-bar' data-tooltip-id='progress-tooltip'>
                    <div className='progress-percent' style={{width: `${calculateAttendancePercent()}%`, backgroundColor: '#0586c5'}} />
                </div>
            </>
            }

            <div className='results-container'>

                {loading && 
                    <div className='loading-users'>
                        Fetching users…
                        <img className="spinner" src={`${BuildPath()}/images/spinner.gif`} alt="loading..." />
                    </div>
                }

                <AnimatePresence>
                    {(usersConfirmed.length > 0 || usersDeclined.length > 0|| usersAwaiting.length > 0 || usersAwaitingApproval.length > 0) && (
                        <motion.div
                            className='search-results search-results-users search-results-all search-results-view'
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: '340px' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <SimpleBar onScroll={(() => {})} style={{ maxHeight: 500 }}>
                                <ul>

                                    {usersAwaitingApproval.length > 0 &&
                                        <>
                                            <li className='label'>Awaiting Approval ({usersAwaitingApproval.length})</li>
                                            {usersAwaitingApproval.map(user => {
                                                return (
                                                    <UserResult
                                                        user={user}
                                                        selectedUsers={usersAwaitingApproval}
                                                        setSelectedUsers={() => {}}
                                                        key={user.id}
                                                        viewEdit={true}
                                                        isGatedEvent
                                                        isConfigurable={isEventConfigurable()}
                                                        getInvitees={getInvitees}
                                                        eventWorkflowId={event.eventWorkflowId}
                                                        isProcessing={processingUsers.includes(user.id)}
                                                        setProccessingUsers={() => setProccessingUsers([...processingUsers, user.id])}
                                                        isFull={eventIsFull()}
                                                        eventTimeRemaining={event.maximumCapacityType === 2 ? event.maximumCapacity - timeToDecimal(event.currentTimeAllocated) : undefined}
                                                        isCancelled={event.isCancelled}
                                                    />
                                                )
                                            })}
                                        </>
                                    }

                                    {usersConfirmed.length > 0 &&
                                        <>
                                            <li className='label'>Confirmed ({usersConfirmed.length})</li>
                                            {usersConfirmed.map(user => {
                                                return (
                                                    <UserResult
                                                        user={user}
                                                        selectedUsers={usersConfirmed}
                                                        setSelectedUsers={() => {}}
                                                        key={user.id}
                                                        viewEdit={true}
                                                        isGatedEvent={isGatedEvent}
                                                        isConfigurable={isEventConfigurable()}
                                                        getInvitees={getInvitees}
                                                        eventWorkflowId={event.eventWorkflowId}
                                                        isProcessing={processingUsers.includes(user.id)}
                                                        setProccessingUsers={() => setProccessingUsers([...processingUsers, user.id])}
                                                        isFull={eventIsFull()}
                                                        eventTimeRemaining={event.maximumCapacityType === 2 ? event.maximumCapacity - timeToDecimal(event.currentTimeAllocated) : undefined}
                                                        isCancelled={event.isCancelled}
                                                    />
                                                )
                                            })}
                                        </>
                                    }

                                    {usersDeclined.length > 0 &&
                                        <>
                                            <li className='label'>Not Attending ({usersDeclined.length})</li>
                                            {usersDeclined.map(user => {
                                                return (
                                                    <UserResult
                                                        user={user}
                                                        selectedUsers={usersDeclined}
                                                        setSelectedUsers={() => {}}
                                                        key={user.id}
                                                        viewEdit={true}
                                                        isGatedEvent={isGatedEvent}
                                                        getInvitees={getInvitees}
                                                        eventWorkflowId={event.eventWorkflowId}
                                                        isProcessing={processingUsers.includes(user.id)}
                                                        setProccessingUsers={() => setProccessingUsers([...processingUsers, user.id])}
                                                        isFull={eventIsFull()}
                                                        eventTimeRemaining={event.maximumCapacityType === 2 ? event.maximumCapacity - timeToDecimal(event.currentTimeAllocated) : undefined}
                                                        isCancelled={event.isCancelled}
                                                    />
                                                )
                                            })}
                                        </>
                                    }

                                    {usersAwaiting.length > 0 &&
                                        <>
                                            <li className='label'>No Response ({usersAwaiting.length})</li>
                                            {usersAwaiting.map(user => {
                                                return (
                                                    <UserResult
                                                        user={user}
                                                        selectedUsers={usersAwaiting}
                                                        setSelectedUsers={() => {}}
                                                        key={user.id}
                                                        viewEdit={true}
                                                        isAwaiting
                                                        getInvitees={getInvitees}
                                                        eventWorkflowId={event.eventWorkflowId}
                                                        isProcessing={processingUsers.includes(user.id)}
                                                        setProccessingUsers={() => setProccessingUsers([...processingUsers, user.id])}
                                                        isFull={eventIsFull()}
                                                        eventTimeRemaining={event.maximumCapacityType === 2 ? event.maximumCapacity - timeToDecimal(event.currentTimeAllocated) : undefined}
                                                        isCancelled={event.isCancelled}
                                                    />
                                                )
                                            })}
                                        </>
                                    }

                                </ul>
                            </SimpleBar>
                        </motion.div>
                    )}
                </AnimatePresence>

                {showConfirmationModal && <ModalWrapper className='archive-event-modal gated' isOpen={showConfirmationModal} escClose={false}  onRequestClose={() => {}} hideCloseButton={true}>    
                    <p>Closing this event will halt people from being able to respond to this event. This can be undone by setting registrations back to ‘Open’.</p>
                    <div className='archive-modal-button-wrapper gated-event'>
                        <button onClick={() => setShowConfirmationModal(false)}>Cancel</button>
                        <button className='archive' onClick={() => {
                            setShowConfirmationModal(false);
                            setRegistrationStatus('Closed');
                        }}>Continue</button>
                    </div>
                </ModalWrapper>}  

                <ToastNotification show={toast?.show} closeToast={() => setToast({...toast, show: false})} messageType={toast?.type}>
                    {toast?.message}
                </ToastNotification>        
            </div>
        </div>
    );
};

export default InviteesViewEdit;

